/**
 * Project Card Component
 * Displays a single project card with image, title, date, and link
 * Uses GraphQL Codegen types for type safety
 * Style adapted from news-card.tsx
 */

'use client';

import { toAbsoluteUrl } from '@/utils/imageUtils';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';

import type { ProjectTeaserType } from '../../types/project';

interface ProjectCardProps {
  project: ProjectTeaserType;
  searchTerm?: string; // Search term for highlighting (optional)
}

/**
 * Format date from timestamp
 */
function formatDate(timestamp: any): string | null {
  if (!timestamp) {
    return null;
  }

  try {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

/**
 * Get image URL from project item
 */
function getImageUrl(project: ProjectTeaserType): string | null {
  const imageUrlRaw =
    project.projectImage?.processed || project.projectImage?.value;

  if (!imageUrlRaw || !imageUrlRaw.trim()) {
    return null;
  }

  // If it contains HTML img tag, extract src attribute
  if (imageUrlRaw.includes('<img')) {
    const srcMatch = imageUrlRaw.match(/src=["']([^"']+)["']/i);
    if (srcMatch && srcMatch[1] && srcMatch[1].trim()) {
      const extractedUrl = srcMatch[1].trim();
      // If extracted URL is absolute, use it directly
      if (/^https?:\/\//i.test(extractedUrl)) {
        return extractedUrl;
      }
      // Otherwise, convert to absolute using backend
      const convertedUrl = toAbsoluteUrl(extractedUrl, true);
      return convertedUrl && convertedUrl.trim() ? convertedUrl : null;
    }
  }
  // If it's already an absolute URL, use it directly
  else if (/^https?:\/\//i.test(imageUrlRaw)) {
    return imageUrlRaw.trim();
  }
  // Otherwise, treat as relative path and convert to absolute
  else {
    const convertedUrl = toAbsoluteUrl(imageUrlRaw, true);
    return convertedUrl && convertedUrl.trim() ? convertedUrl : null;
  }

  return null;
}

/**
 * Get link URL from project item
 */
function getLinkUrl(project: ProjectTeaserType): string | null {
  const projectLink = project.projectLink?.url || '';
  if (!projectLink) {
    return null;
  }

  // Remove internal: prefix if present
  return projectLink.replace(/^internal:/, '');
}

/**
 * Extract text excerpt from HTML
 */
function getTextExcerpt(
  html: string | null | undefined,
  maxLength = 150
): string {
  if (!html) return '';
  const text = html.replace(/<[^>]*>/g, '');
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
}

export function ProjectCard({
  project,
  searchTerm,
}: ProjectCardProps) {
  const projectName = project.projectName || project.title || 'Project';
  const imageUrl = getImageUrl(project);
  const linkUrl = getLinkUrl(project);
  const projectDate = formatDate(project.projectDate?.timestamp);

  // Get excerpt from projectDescription or body
  const excerpt = getTextExcerpt(
    project.projectDescription?.processed ||
      project.projectDescription?.value ||
      project.body?.summary ||
      null
  );

  // Handle click for external links
  const handleCardClick = (e: React.MouseEvent) => {
    if (linkUrl) {
      e.preventDefault();
      window.open(linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const cardContent = (
    <>
      {/* Image */}
      {imageUrl ? (
        <div className="relative aspect-video overflow-hidden">
          <Image
            src={imageUrl}
            alt={projectName}
            fill
            className="h-full w-full object-contain p-4 transition-transform duration-300 group-hover:scale-105"
          />
        </div>
      ) : (
        <div className="relative aspect-video flex items-center justify-center">
          <span className="text-muted-foreground text-sm sr-only">No image</span>
        </div>
      )}

      {/* Content */}
      <div className="flex flex-1 flex-col p-4">
        <h3 className="mb-2 line-clamp-2 text-lg font-semibold leading-tight transition-colors group-hover:text-primary">
          {projectName}
        </h3>

        {/* Excerpt */}
        {excerpt && (
          <p className="line-clamp-3 flex-1 text-sm text-muted-foreground">
            {excerpt}
          </p>
        )}

        {/* Read more link */}
        {linkUrl && (
          <div className="flex items-center gap-2 text-sm font-medium text-primary group-hover:gap-3 transition-all mt-auto pt-6">
            Read more
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        )}
      </div>
    </>
  );

  // If link exists, card opens in new tab
  if (linkUrl) {
    return (
      <div
        onClick={handleCardClick}
        className="group flex flex-col h-full cursor-pointer overflow-hidden rounded bg-slate-500/5 shadow-sm transition-all hover:shadow-lg"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick(e as any);
          }
        }}
      >
        {cardContent}
      </div>
    );
  }

  // No link - just display card
  return (
    <div className="group block h-full overflow-hidden rounded-lg border bg-card shadow-sm transition-all hover:shadow-lg">
      {cardContent}
    </div>
  );
}

